import type { OrderbookCompression } from '../components/screener/orderbookCompression'

export type ScreenerOrderbookPrefs = {
  selectedSourceIds: string[]
  collapsedSourceIds: string[]
  compression: OrderbookCompression
  compressionOverrides: Record<string, OrderbookCompression>
}

export const DEFAULT_ORDERBOOK_COMPRESSION: OrderbookCompression = { mode: 'percent', value: null }

function parseJson(raw: string | null): unknown {
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function normalizeSourceIds(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return [...new Set(value.filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim().toLowerCase()).filter(Boolean))]
}

function normalizeCompression(value: unknown): OrderbookCompression | null {
  if (!isRecord(value)) return null
  if (value.mode === 'percent' && value.value === null) return { mode: 'percent', value: null }
  if (typeof value.value !== 'number' || !Number.isFinite(value.value)) return null
  if (value.mode === 'percent' && value.value >= 0) return { mode: 'percent', value: value.value }
  if (value.mode === 'multiplier' && value.value >= 1) return { mode: 'multiplier', value: value.value }
  return null
}

function migrateCompressionPercentage(value: unknown): OrderbookCompression {
  const percentage = typeof value === 'number' || (typeof value === 'string' && value.trim())
    ? Number(value) : NaN
  return { mode: 'percent', value: Number.isFinite(percentage) && percentage >= 0 ? percentage : null }
}

function normalizeCompressionOverrides(value: unknown): Record<string, OrderbookCompression> {
  if (!isRecord(value)) return {}
  const overrides: Record<string, OrderbookCompression> = {}
  for (const [key, setting] of Object.entries(value)) {
    const sourceId = key.trim().toLowerCase()
    const compression = normalizeCompression(setting)
    // Retain settings for temporarily unavailable sources, but require a full source ID.
    if (/^[^:\s]+:[^:\s]+$/.test(sourceId) && compression) overrides[sourceId] = compression
  }
  return overrides
}

/** Restore one account's complete state, including pre-compression source-only saves. */
export function parseScreenerOrderbookPrefs(
  raw: string | null,
  legacySourcesRaw: string | null = null,
): ScreenerOrderbookPrefs {
  const parsed = parseJson(raw)
  if (!isRecord(parsed)) {
    return {
      selectedSourceIds: normalizeSourceIds(parseJson(legacySourcesRaw)),
      collapsedSourceIds: [],
      compression: { ...DEFAULT_ORDERBOOK_COMPRESSION },
      compressionOverrides: {},
    }
  }
  return {
    selectedSourceIds: normalizeSourceIds(parsed.selectedSourceIds),
    collapsedSourceIds: normalizeSourceIds(parsed.collapsedSourceIds),
    compression: normalizeCompression(parsed.compression) ?? migrateCompressionPercentage(parsed.compressionPct),
    compressionOverrides: normalizeCompressionOverrides(parsed.compressionOverrides),
  }
}
